import { makeStyles } from "@mui/styles";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EmptyFBA } from "../components/fba/EmptyFBA";
import { FBA } from "../components/fba/FBA";

import { FBASidePanel } from "../components/fba/FBASidePanel";
import { useSocket } from "../context/useSocket";
import { selectFBA } from "../reducers/fbaSlice";
import { setIsLoadingData } from "../reducers/optionsSlice";
import { selectUser } from "../reducers/userSlice";
import { FBAListContainer } from "./FBAListContainer";
import { fbaConversionFactors } from "../components/fba/fbaConversionFactors";
import { c_TO_F } from "../helpers/conversionFactors";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    backgroundColor: theme.palette.grey[100],
    border: 0,
    color: 'text.primary',
    height: '100vh',
    width: 'calc(100% - 60px)',
    paddingTop: 30,
    marginLeft: 60,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
}));

const KEY_F9 = 120;

export const FBAContainer = () => {
  
  const dispatch = useDispatch();
  const classes = useStyles();
  const user = useSelector(selectUser);
  const fba = useSelector(selectFBA);
  const currentFBA = fba?.byId?.[fba?.currentFBAId];

  const [fbaSettings, setFbaSettings] = useState(currentFBA?.settings);
  const [fbaUnits, setFBAUnits] = useState(currentFBA?.settings?.units?.value || 'field');

  const { socket } = useSocket();

  if (document.title !== ('FBA | SSD')) document.title = ('FBA | SSD');

  return (
    !user?.value?.fbaAccess ?
    <div style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
      Please add FBA subscription to your account
    </div> :
    <div className={classes.root} tabIndex={0} onKeyDown={handleKeyDown}>
      <FBAListContainer />
      {
        currentFBA?.fbaData ?
        <>
          <FBASidePanel fbaSettings={fbaSettings} setFbaSettings={setFbaSettings} fbaUnits={fbaUnits} setFBAUnits={setFBAUnits} />
          <FBA fbaUnits={fbaUnits} setFBAUnits={setFBAUnits} />
        </>
        :
        <EmptyFBA email={user?.value?.email} />
      }
    </div>
  );

  function handleKeyDown(e) {
    if (e.keyCode === KEY_F9) {
      console.log('Calculating FBA...');
      handleCalculateFBAClick(currentFBA, fbaSettings, socket, user?.value?.email, dispatch);
    };
  };

  // async function handleCalculateFBAClick(fba) {
  //   try {
  //     let newFba = {...fba};
  //     newFba.settings = {
  //       ...fbaSettings, 
  //       multiPhaseFitIntercept: fba?.settings?.multiPhaseFitIntercept, 
  //       linearFlowFitIntercept: fba?.settings?.linearFlowFitIntercept,
  //       fractureDepletionFitIntercept: fba?.settings?.fractureDepletionFitIntercept,
  //       linearFlowEndTmb: fba?.settings?.linearFlowEndTmb,
  //       sqrtTimeFitSlope: fba?.settings?.sqrtTimeFitSlope,
  //       sqrtTimeFitIntercept: fba?.settings?.sqrtTimeFitIntercept,
  //       fmbFitSlope: fba?.settings?.fmbFitSlope,
  //       fmbFitIntercept: fba?.settings?.fmbFitIntercept
  //     };

  //     if (socket && socket.readyState === 1) {
  //       socket.send(JSON.stringify({messageType: 'fba', payload: newFba, userEmail: user?.value?.email}));
  //     };

  //     dispatch(setIsLoadingData(true));
  //   } catch (error) {
  //     console.log(error);
  //     return;
  //   };
  // };
};

export async function handleCalculateFBAClick(fba, fbaSettings, socket, userEmail, dispatch) {
  try {
    const newFba = {...fba};
    const newSettings = JSON.parse(JSON.stringify({
      ...fbaSettings, 
      multiPhaseFitIntercept: fba?.settings?.multiPhaseFitIntercept, 
      linearFlowFitIntercept: fba?.settings?.linearFlowFitIntercept,
      fractureDepletionFitIntercept: fba?.settings?.fractureDepletionFitIntercept,
      linearFlowEndTmb: fba?.settings?.linearFlowEndTmb,
      sqrtTimeFitSlope: fba?.settings?.sqrtTimeFitSlope,
      sqrtTimeFitIntercept: fba?.settings?.sqrtTimeFitIntercept,
      fmbFitSlope: fba?.settings?.fmbFitSlope,
      fmbFitIntercept: fba?.settings?.fmbFitIntercept
    }));

    
    // convert values back from metric to field units before calculating fba
    if (newSettings?.units?.value === 'metric') {
      for (const param of Object.keys(newSettings)) {
        let value = newSettings?.[param]?.value;

        if (!value) continue;

        if (param === 't_r' || param === 'wht' || param === 'tSep') {
          value = parseInt(c_TO_F(value));
        } else if (param === 'geothermalGradient') {
          value = +((value * (9 / 5) / 3.28)?.toFixed(3));
          // value = +(((value) * (5 / 9) * 3.28)?.toFixed(3));
        }  else {
          if (!fbaConversionFactors?.[param]) continue;
          value = newSettings?.[param]?.value / fbaConversionFactors?.[param];
        };

        newSettings[param] = {...newSettings?.[param], value};
      };
    };
    
    // only re-calculate solids and water sg for fbas last modified after Feb. 5 2024
    if (new Date(fba?.lastModified)?.getTime() > new Date('2024-02-05 00:00:00')?.getTime()) {
      // calculate solids wt%
      const solids_bbt = calculateWtPercentSolids('bbt', newFba?.fbaData);
      const solids_abt = calculateWtPercentSolids('abt', newFba?.fbaData);
      newSettings.solids_bbt.value = solids_bbt;
      newSettings.solids_abt.value = solids_abt;
      newSettings.wtPercentSolidsMatrixInflow.value = solids_abt;

      console.log('Recalculating water SG...');
      // calculate sgWater
      const averageSolids = (solids_bbt + solids_abt) / 2;
      const bW = 1;
      const sgWater = ((62.368 + 47 * averageSolids) / bW) / 62.368;
      newSettings.sgWater.value = sgWater;
    };

    // calculate s_o
    const s_o = 1 - newSettings?.s_w?.value;
    newSettings.s_o.value = s_o;


    // calculate rB
    const rB = newSettings?.reservoirType?.value === 1 ? newSettings?.gor?.value :  (1 / (newSettings?.cgr?.value / 1e6));
    newSettings.rB.value = rB;


    newFba.settings = newSettings;

    if (socket && socket.readyState === 1) {
      socket.send(JSON.stringify({ messageType: 'fba', payload: newFba, userEmail }));
    };

    dispatch(setIsLoadingData(true));
  } catch (error) {
    console.log(error);
    return;
  };
};

/**
 * Calculates wt% solids before or after breakthrouh
 *
 * @param {string} type - Type of calculation ('bbt' or 'abt').
 * @param {Array<{}>} fbaData - FBA input data array.
 * @returns {number} - Weight percent solids as a decimal.
 * @throws {Error} - Throws an error if either the type is wrong of fbaData is missing.
*/
function calculateWtPercentSolids(type, fbaData) {
  try {

    if (!['bbt', 'abt']?.includes(type)) throw new Error('Type must be either bbt or abt');
    if (!fbaData || !fbaData?.length > 0) throw new Error('FBA input data is required');

    let salinity = null;
    const defaultSalinity = type === 'bbt' ? 0.05 : 0.1;

    if (type === 'bbt') { // calculate BBT wt% solids
      // determine if single phase is present
      let singlePhaseIndex = 0;

      for (let i = 0; i < fbaData?.length; i++) {
        const {water, oil, gas, condensate} = fbaData[i];
        if (water > 1 && ((oil || 0) + (gas || 0) + (condensate || 0)) < 1) {
          singlePhaseIndex = i;
        } else {
          break;
        };
      };

      if (singlePhaseIndex > 11) {
        const dataSlice = fbaData?.slice(0, singlePhaseIndex + 1);
        salinity = dataSlice?.reduce((tot, curr) => curr.salinity / 10000 + tot, 0) / dataSlice?.length / 100;
      } else {
        const dataSlice = fbaData?.slice(0, 10);
        salinity = dataSlice?.reduce((tot, curr) => curr.salinity / 10000 + tot, 0) / dataSlice?.length / 100;
      };

      
    } else if (type === 'abt') { // calculate ABT wt% solids
      salinity = Math.max(...fbaData?.map(d => d.salinity)) / 10000 / 100 * 1.2;
    };

    if (!isNaN(salinity) && salinity > 0 && isFinite(salinity)) {
      return salinity;
    } else {
      return defaultSalinity;
    };

  } catch(error) {
    console.log(error);
    throw new Error('Error calculating weight percent solids');
  };
};
